import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Empty, Tag } from "antd";
// REDUX
import { useSelector } from "react-redux";
// I18N
import { useTranslation } from "react-i18next";
// FONT AWESOME
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// INTERFACES
import {
	Ln2ToModbusEntry,
	RequestKind,
	UInt16,
	UByte,
} from "../../../../../interfaces/AdapterCdoModbus.interface";
import { RootState } from "../../../../../app/store";
// CONSTANTS
import { DEFAULT_MODBUS_LN2_TO_MSG } from "../../../../../constants/adapter";
// COMPONENTS
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import FormSelectMultipleBoolean from "../../../../components/form/FormSelectMultipleBoolean";
import FormSelectMultipleNumbers from "../../../../components/form/FormSelectMultipleNumbers";
import ComponentFrontEndMsg from "./ComponentFrontEndMsg";
import ComponentMsg from "./ComponentMsg";
import FormSelectRequestMessageRequest from "../../../../components/form/FormSelectRequestMessageRequest";

export default function ComponentLn2ToMsg({
	ln2ToMsgMap,
	setLn2ToMsgMap,
}: {
	ln2ToMsgMap: Ln2ToModbusEntry[];
	setLn2ToMsgMap: (newValue: Ln2ToModbusEntry[]) => void;
}) {
	const [t] = useTranslation("global");
	const { theme } = useSelector((state: RootState) => state.home);

	const [ln2ToMsg, setLn2ToMsg] = useState<Ln2ToModbusEntry>({
		...DEFAULT_MODBUS_LN2_TO_MSG,
	});

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeReadCoilsRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "ReadCoilsRequest", address: 0, quantity: 0 },
			},
		}));
	};

	const onChangeReadDiscreteInputsRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "ReadDiscreteInputsRequest", address: 0, quantity: 0 },
			},
		}));
	};

	const onChangeReadHoldingRegistersRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: {
					kind: "ReadHoldingRegistersRequest",
					address: 0,
					quantity: 0,
				},
			},
		}));
	};

	const onChangeReadInputRegistersRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "ReadInputRegistersRequest", address: 0, quantity: 0 },
			},
		}));
	};

	const onChangeReadWriteMultipleRegistersRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: {
					kind: "ReadWriteMultipleRegistersRequest",
					read_address: 0,
					read_quantity: 0,
					write_address: 0,
					data: [],
				},
			},
		}));
	};

	const onChangeWriteSingleCoilRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "WriteSingleCoilRequest", address: 0, value: false },
			},
		}));
	};

	const onChangeWriteMultipleCoilRequests = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "WriteMultipleCoilRequests", address: 0, data: [] },
			},
		}));
	};

	const onChangeWriteSingleRegisterRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "WriteSingleRegisterRequest", address: 0, value: 0 },
			},
		}));
	};

	const onChangeWriteMultipleRegistersRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: {
					kind: "WriteMultipleRegistersRequest",
					address: 0,
					data: [],
				},
			},
		}));
	};

	const onChangeReportServerIdRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "ReportServerIdRequest" },
			},
		}));
	};

	const onChangeMaskWriteRegisterRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: {
					kind: "MaskWriteRegisterRequest",
					address: 0,
					and_mask: 0,
					or_mask: 0,
				},
			},
		}));
	};

	const onChangeCustomRequest = () => {
		setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
			...prev,
			val: {
				message: { kind: "CustomRequest", function_code: 0, data: [] },
			},
		}));
	};

	const onChangeRequestKind = (newValue: RequestKind) => {
		switch (newValue) {
			case "ReadCoilsRequest":
				onChangeReadCoilsRequest();
				break;
			case "ReadDiscreteInputsRequest":
				onChangeReadDiscreteInputsRequest();
				break;
			case "ReadHoldingRegistersRequest":
				onChangeReadHoldingRegistersRequest();
				break;
			case "ReadInputRegistersRequest":
				onChangeReadInputRegistersRequest();
				break;
			case "ReadWriteMultipleRegistersRequest":
				onChangeReadWriteMultipleRegistersRequest();
				break;
			case "WriteSingleCoilRequest":
				onChangeWriteSingleCoilRequest();
				break;
			case "WriteMultipleCoilRequests":
				onChangeWriteMultipleCoilRequests();
				break;
			case "WriteSingleRegisterRequest":
				onChangeWriteSingleRegisterRequest();
				break;
			case "WriteMultipleRegistersRequest":
				onChangeWriteMultipleRegistersRequest();
				break;
			case "ReportServerIdRequest":
				onChangeReportServerIdRequest();
				break;
			case "MaskWriteRegisterRequest":
				onChangeMaskWriteRegisterRequest();
				break;
			case "CustomRequest":
				onChangeCustomRequest();
				break;
			default:
				break;
		}
	};

	const onClickAddNewMessage = () => {
		let copy: Ln2ToModbusEntry[] = structuredClone(ln2ToMsgMap);
		copy = [...copy, ln2ToMsg];
		setLn2ToMsgMap(copy);
	};

	const onClickDeleteMessage = (indexToDelete: number) => {
		const copy = ln2ToMsgMap.filter((_, index) => index !== indexToDelete);
		setLn2ToMsgMap(copy);
	};

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='container'>
			{/* *********************************************** */}
			{/* MAP LN2 MESSAGES / DELETE MESSAGE */}
			{ln2ToMsgMap.length === 0 ? (
				<Empty description='No messages added' />
			) : (
				<>
					{ln2ToMsgMap.map((item: Ln2ToModbusEntry, index: number) => (
						<div className={`drawer__box__${theme} row mt-2`} key={index}>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										LN2 to Message
									</div>
									<div className='generalStyles__width100'>
										<div className='generalStyles__flex'>
											<div className='generalStyles__width100'>
												<Tag
													color='cyan'
													style={{ textAlign: "center", width: "100%" }}
												>
													{item.val.message.kind}
												</Tag>
											</div>
											<div>
												<Button
													className='buttonStyle__29'
													shape='circle'
													style={{ marginLeft: "10px", marginTop: "-5px" }}
													icon={
														<FontAwesomeIcon
															icon={faTrash}
															onClick={() => onClickDeleteMessage(index)}
														/>
													}
												/>
											</div>
										</div>
									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
					))}
				</>
			)}

			<div className={`drawer__box__${theme} row mt-3`}>
				<FormSelectRequestMessageRequest
					disabledInput={false}
					inputValue={ln2ToMsg.val.message.kind}
					inputOnChange={(value: RequestKind) => onChangeRequestKind(value)}
				/>

				{ln2ToMsg.val.message.kind === "ReadCoilsRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadCoilsRequest",
											address: newValue,
											quantity:
												prev.val.message.kind === "ReadCoilsRequest"
													? prev.val.message.quantity
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.quantity}
							inputLabelName='quantity'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadCoilsRequest",
											address:
												prev.val.message.kind === "ReadCoilsRequest"
													? prev.val.message.address
													: 0,
											quantity: newValue,
										},
									},
								}));
							}}
							inputVariableName='quantity'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "ReadDiscreteInputsRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadDiscreteInputsRequest",
											address: newValue,
											quantity:
												prev.val.message.kind === "ReadDiscreteInputsRequest"
													? prev.val.message.quantity
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.quantity}
							inputLabelName='quantity'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadDiscreteInputsRequest",
											address:
												prev.val.message.kind === "ReadDiscreteInputsRequest"
													? prev.val.message.address
													: 0,
											quantity: newValue,
										},
									},
								}));
							}}
							inputVariableName='quantity'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "ReadHoldingRegistersRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadHoldingRegistersRequest",
											address: newValue,
											quantity:
												prev.val.message.kind === "ReadHoldingRegistersRequest"
													? prev.val.message.quantity
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.quantity}
							inputLabelName='quantity'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadHoldingRegistersRequest",
											address:
												prev.val.message.kind === "ReadHoldingRegistersRequest"
													? prev.val.message.address
													: 0,
											quantity: newValue,
										},
									},
								}));
							}}
							inputVariableName='quantity'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "ReadInputRegistersRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadInputRegistersRequest",
											address: newValue,
											quantity:
												prev.val.message.kind === "ReadInputRegistersRequest"
													? prev.val.message.quantity
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.quantity}
							inputLabelName='quantity'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadInputRegistersRequest",
											address:
												prev.val.message.kind === "ReadInputRegistersRequest"
													? prev.val.message.address
													: 0,
											quantity: newValue,
										},
									},
								}));
							}}
							inputVariableName='quantity'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "ReadWriteMultipleRegistersRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.read_address}
							inputLabelName='read_address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadWriteMultipleRegistersRequest",
											read_address: newValue,
											read_quantity:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_quantity
													: 0,
											write_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.write_address
													: 0,
											data:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='read_address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.read_quantity}
							inputLabelName='read_quantity'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadWriteMultipleRegistersRequest",
											read_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_address
													: 0,
											read_quantity: newValue,
											write_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.write_address
													: 0,
											data:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='read_quantity'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.write_address}
							inputLabelName='write_address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadWriteMultipleRegistersRequest",
											read_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_address
													: 0,
											read_quantity:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_quantity
													: 0,
											write_address: newValue,
											data:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='write_address'
							
						/>
						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={ln2ToMsg.val.message.data}
							inputOnChange={(newValue: UInt16[]) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "ReadWriteMultipleRegistersRequest",
											read_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_address
													: 0,
											read_quantity:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.read_quantity
													: 0,
											write_address:
												prev.val.message.kind ===
												"ReadWriteMultipleRegistersRequest"
													? prev.val.message.write_address
													: 0,
											data: newValue,
										},
									},
								}));
							}}
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "WriteSingleCoilRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteSingleCoilRequest",
											address: newValue,
											value:
												prev.val.message.kind === "WriteSingleCoilRequest"
													? prev.val.message.value
													: false,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputBooleanSwitch
							value={ln2ToMsg.val.message.value}
							onChangeSwitch={(newValue: boolean) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteSingleCoilRequest",
											address:
												prev.val.message.kind === "WriteSingleCoilRequest"
													? prev.val.message.address
													: 0,
											value: newValue,
										},
									},
								}));
							}}
							labelName='Value'
							variableName='value'
							isRequired
							disabledInput={false}
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "WriteMultipleCoilRequests" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteMultipleCoilRequests",
											address: newValue,
											data:
												prev.val.message.kind === "WriteMultipleCoilRequests"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormSelectMultipleBoolean
							disabledInput={false}
							inputValue={ln2ToMsg.val.message.data}
							inputOnChange={(newValue: boolean[]) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteMultipleCoilRequests",
											address:
												prev.val.message.kind === "WriteMultipleCoilRequests"
													? prev.val.message.address
													: 0,
											data: newValue,
										},
									},
								}));
							}}
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "WriteSingleRegisterRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteSingleRegisterRequest",
											address: newValue,
											value:
												prev.val.message.kind === "WriteSingleRegisterRequest"
													? prev.val.message.value
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.value}
							inputLabelName='value'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteSingleRegisterRequest",
											address:
												prev.val.message.kind === "WriteSingleRegisterRequest"
													? prev.val.message.address
													: 0,
											value: newValue,
										},
									},
								}));
							}}
							inputVariableName='value'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "WriteMultipleRegistersRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteMultipleRegistersRequest",
											address: newValue,
											data:
												prev.val.message.kind ===
												"WriteMultipleRegistersRequest"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={ln2ToMsg.val.message.data}
							inputOnChange={(newValue: UInt16[]) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "WriteMultipleRegistersRequest",
											address:
												prev.val.message.kind ===
												"WriteMultipleRegistersRequest"
													? prev.val.message.address
													: 0,
											data: newValue,
										},
									},
								}));
							}}
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "MaskWriteRegisterRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "MaskWriteRegisterRequest",
											address: newValue,
											and_mask:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.and_mask
													: 0,
											or_mask:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.or_mask
													: 0,
										},
									},
								}));
							}}
							inputVariableName='address'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.and_mask}
							inputLabelName='and_mask'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "MaskWriteRegisterRequest",
											address:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.address
													: 0,
											and_mask: newValue,
											or_mask:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.or_mask
													: 0,
										},
									},
								}));
							}}
							inputVariableName='and_mask'
							
						/>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.or_mask}
							inputLabelName='or_mask'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "MaskWriteRegisterRequest",
											address:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.address
													: 0,
											and_mask:
												prev.val.message.kind === "MaskWriteRegisterRequest"
													? prev.val.message.and_mask
													: 0,
											or_mask: newValue,
										},
									},
								}));
							}}
							inputVariableName='or_mask'
							
						/>
					</>
				)}

				{ln2ToMsg.val.message.kind === "CustomRequest" && (
					<>
						<FormInputNumber
							inputValue={ln2ToMsg.val.message.function_code}
							inputLabelName='function_code'
							inputOnChange={(newValue: number) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "CustomRequest",
											function_code: newValue,
											data:
												prev.val.message.kind === "CustomRequest"
													? prev.val.message.data
													: [],
										},
									},
								}));
							}}
							inputVariableName='function_code'
							
						/>
						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={ln2ToMsg.val.message.data as UByte[]}
							inputOnChange={(newValue: UInt16[]) => {
								setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
									...prev,
									val: {
										message: {
											kind: "CustomRequest",
											function_code:
												prev.val.message.kind === "CustomRequest"
													? prev.val.message.function_code
													: 0,
											data: newValue,
										},
									},
								}));
							}}
						/>
					</>
				)}
			</div>

			{/* *********************************************** */}
			{/* FRONTEND MESSAGE  */}
			<div className={`drawer__box__${theme} row mt-3`}>
				<ComponentFrontEndMsg
					value={ln2ToMsg.key}
					onChange={(newValue) => {
						setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
							...prev,
							key: newValue,
						}));
					}}
					disabledInput={false}
				/>
				<ComponentMsg
					value={ln2ToMsg.key}
					onChange={(newValue) => {
						setLn2ToMsg((prev: Ln2ToModbusEntry) => ({
							...prev,
							key: newValue,
						}));
					}}
					disabledInput={false}
				/>
			</div>

			{/* *********************************************** */}
			{/* ADD BUTTON  */}
			<div className='col-md-12 mt-3'>
				<div className='generalStyles__flex'>
					<div className='generalStyles__inputFlexNameLarge'>
						<span className='generalStyles__inputFlexRequired' />
					</div>
					<div className='generalStyles__width100'>
						<Button
							onClick={onClickAddNewMessage}
							className='buttonStyle__28 generalStyles__width100'
						>
							<FontAwesomeIcon icon={faPlus} className='generalStyles__mrFix' />
							{t("general.add")}
						</Button>
					</div>
					<div className='generalStyles__infoTooltipIconBlank' />
				</div>
			</div>
		</div>
	);
}
